import { BaseService, ServiceResponse } from './base.service';
import type { 
  PerformanceZonesSummary,
  CourseDiscoveryItem
} from '@/types/performance-zones';

export interface PerformanceZonesCacheData {
  performanceData: PerformanceZonesSummary;
  courseDiscovery: CourseDiscoveryItem[];
  starredCourses: string[];
  lastUpdated: number;
  version: number;
}

/**
 * Performance Zones Cache Service
 * 
 * Simple in-memory caching for Performance Zones data:
 * - Fast memory-based cache (no database needed)
 * - Smart cache invalidation
 * - Background refresh for stale data
 * - Automatic cleanup of expired entries
 */
export class PerformanceZonesCacheService extends BaseService {
  private static readonly CACHE_KEY_PREFIX = 'perf_zones';
  private static readonly DEFAULT_TTL_MINUTES = 15; // Shorter TTL for memory-only cache
  private static readonly PRELOAD_THRESHOLD_MINUTES = 5;
  
  // In-memory cache - simple and fast
  private memoryCache = new Map<string, {
    data: PerformanceZonesCacheData;
    expiresAt: number;
    lastAccessed: number;
  }>();

  /**
   * Get cached Performance Zones data from memory cache
   */
  getCachedData(
    userId: string,
    options?: {
      maxAge?: number; // milliseconds
      forceRefresh?: boolean;
    }
  ): PerformanceZonesCacheData | null {
    const cacheKey = this.generateCacheKey(userId);
    
    if (options?.forceRefresh) {
      console.log('🔄 [PERF-CACHE] Force refresh requested, skipping cache');
      return null;
    }

    const cached = this.getFromMemoryCache(cacheKey, options?.maxAge);
    if (cached) {
      console.log('⚡ [PERF-CACHE] Retrieved from memory cache');
      return cached;
    }

    console.log('❌ [PERF-CACHE] No valid cache found');
    return null;
  }

  /**
   * Store data in memory cache
   */
  setCachedData(
    userId: string, 
    data: PerformanceZonesCacheData,
    options?: {
      ttlMinutes?: number;
    }
  ): void {
    const cacheKey = this.generateCacheKey(userId);
    const ttlMinutes = options?.ttlMinutes || PerformanceZonesCacheService.DEFAULT_TTL_MINUTES;

    this.setMemoryCache(cacheKey, data, ttlMinutes);
    console.log(`✅ [PERF-CACHE] Cached data for user ${userId} with TTL ${ttlMinutes} minutes`);
  }

  /**
   * Invalidate cache for specific user
   */
  invalidateUserCache(userId: string): void {
    const cacheKey = this.generateCacheKey(userId);
    this.memoryCache.delete(cacheKey);
    console.log(`🗑️ [PERF-CACHE] Invalidated cache for user ${userId}`);
  }

  /**
   * Clean up expired cache entries
   */
  cleanupExpiredCache(): { removedCount: number } {
    let removedCount = 0;
    const now = Date.now();
    
    for (const [key, entry] of this.memoryCache) {
      if (entry.expiresAt < now) {
        this.memoryCache.delete(key);
        removedCount++;
      }
    }
    
    if (removedCount > 0) {
      console.log(`🧹 [PERF-CACHE] Cleaned up ${removedCount} expired entries`);
    }

    return { removedCount };
  }

  /**
   * Get simple cache metrics
   */
  getCacheMetrics(): {
    memoryCacheSize: number;
    expiredEntries: number;
  } {
    const now = Date.now();
    let expiredCount = 0;
    
    for (const [key, entry] of this.memoryCache) {
      if (entry.expiresAt < now) {
        expiredCount++;
      }
    }

    return {
      memoryCacheSize: this.memoryCache.size,
      expiredEntries: expiredCount
    };
  }

  /**
   * Check if cache needs refresh based on data staleness
   */
  shouldRefreshCache(userId: string): {
    shouldRefresh: boolean;
    reason: string;
    cacheAge: number; // minutes
  } {
    const cached = this.getCachedData(userId);
    
    if (!cached) {
      return {
        shouldRefresh: true,
        reason: 'No cache found',
        cacheAge: 0
      };
    }

    const cacheAge = (Date.now() - cached.lastUpdated) / (1000 * 60);
    const preloadThreshold = PerformanceZonesCacheService.PRELOAD_THRESHOLD_MINUTES;

    if (cacheAge > preloadThreshold) {
      return {
        shouldRefresh: true,
        reason: `Cache older than ${preloadThreshold} minutes`,
        cacheAge
      };
    }

    return {
      shouldRefresh: false,
      reason: 'Cache is fresh',
      cacheAge
    };
  }

  // Private helper methods

  private generateCacheKey(userId: string): string {
    return `${PerformanceZonesCacheService.CACHE_KEY_PREFIX}_${userId}`;
  }

  private getFromMemoryCache(
    cacheKey: string, 
    maxAge?: number
  ): PerformanceZonesCacheData | null {
    const cached = this.memoryCache.get(cacheKey);
    if (!cached) return null;

    const now = Date.now();
    
    // Check expiration
    if (cached.expiresAt < now) {
      this.memoryCache.delete(cacheKey);
      return null;
    }

    // Check max age if specified
    if (maxAge && (now - cached.lastAccessed) > maxAge) {
      return null;
    }

    // Update access tracking
    cached.lastAccessed = now;
    
    return cached.data;
  }

  private setMemoryCache(cacheKey: string, data: PerformanceZonesCacheData, ttlMinutes?: number): void {
    const ttlMs = (ttlMinutes || PerformanceZonesCacheService.DEFAULT_TTL_MINUTES) * 60 * 1000;
    const now = Date.now();
    
    this.memoryCache.set(cacheKey, {
      data,
      expiresAt: now + ttlMs,
      lastAccessed: now
    });
  }
}